/** biome-ignore-all lint/complexity/useSimpleNumberKeys: this rule would messup our color codes like 500_8 => 5008 */
import { alpha, type PaletteOptions } from '@mui/material/styles';
import type { DeepPartial } from 'types/utils';

// ----------------------------------------------------------------------

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
}

interface ChartPaletteOptions {
  violet: string[];
  blue: string[];
  green: string[];
  yellow: string[];
  red: string[];
}

type NavBarColorOptions = {
  menuActiveBg: string;
  menuActiveText: string;
  menuDefaultText: string;
  menuHoverBg: string;
  menuHoverText: string;
  menuSecondaryText: string;
  searchHoverBorder?: string;
};

type NotePaletteOptions = {
  paper: string;
};

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface Palette {
    impersonation: PaletteColor;
    konnect: string;
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
    navBar: NavBarColorOptions;
    note: NotePaletteOptions;
  }
  interface PaletteOptions {
    impersonation: PaletteColor;
    konnect: string;
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
    navBar: NavBarColorOptions;
    note: NotePaletteOptions;
  }
}

declare module '@mui/material' {
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }

  interface ButtonPropsColorOverrides {
    impersonation: true;
  }

  interface ButtonGroupPropsColorOverrides {
    impersonation: true;
  }

  interface IconButtonPropsColorOverrides {
    impersonation: true;
  }
}

// SETUP COLORS
const grey = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  500_8: alpha('#919EAB', 0.08),
  500_12: alpha('#919EAB', 0.12),
  500_16: alpha('#919EAB', 0.16),
  500_24: alpha('#919EAB', 0.24),
  500_32: alpha('#919EAB', 0.32),
  500_48: alpha('#919EAB', 0.48),
  500_56: alpha('#919EAB', 0.56),
  500_80: alpha('#919EAB', 0.8),
};

export const purple = {
  lighter: '#EBD6FD',
  light: '#B985F4',
  main: '#7635dc',
  dark: '#431A9E',
  darker: '#200A69',
  contrastText: '#fff',
};

export const cyan = {
  lighter: '#D1FFFC',
  light: '#76F2FF',
  main: '#1CCAFF',
  dark: '#0E77B7',
  darker: '#053D7A',
  contrastText: grey[800],
};

export const blue = {
  lighter: '#00AAD214',
  light: '#00AAD232',
  main: '#00AAD2',
  dark: '#02749E',
  darker: '#03476E',
  contrastText: '#fff',
};

export const orange = {
  lighter: '#FEF4D4',
  light: '#FED680',
  main: '#FFAE03',
  dark: '#B66816',
  darker: '#793908',
  contrastText: grey[800],
};

export const red = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#fff',
};

export const muted = {
  lighter: '#DFE3E8',
  light: '#C4CDD5',
  main: '#919EAB',
  dark: '#637381',
  darker: '#454F5B',
  contrastText: '#fff',
};

export const green = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
  contrastText: '#fff',
};

// This old material green primary color, referenced as default in some files
export const jade = {
  lighter: '#C8FACD',
  light: '#5BE584',
  main: '#00AB55',
  dark: '#007B55',
  darker: '#005249',
};

export const yellow = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
  contrastText: '#fff',
};

const primary = blue;

const secondary = {
  lighter: '#D6E4FF',
  light: '#84A9FF',
  main: '#3366FF',
  dark: '#1939B7',
  darker: '#091A7A',
  contrastText: '#fff',
};

const info = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
  contrastText: '#fff',
};

const success = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
  contrastText: grey[800], // TODO consider using a green shade instead
};

const warning = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01', // TODO fix Bezold–Brücke shift on yellow colors at low luminosity
  contrastText: grey[800], // TODO consider using a yellow shade instead
};

const error = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
  contrastText: '#fff',
};

const impersonation = {
  lighter: '#FVE0FB',
  light: '#F2B1F4',
  main: '#E877EE',
  dark: '#B500C3',
  darker: '#7A00B4',
  contrastText: '#fff',
};

// Faks Konnect brand color (single brand color, no shades)
const konnect = '#FFC107';

const gradients = {
  primary: createGradient(primary.light, primary.main),
  info: createGradient(info.light, info.main),
  success: createGradient(success.light, success.main),
  warning: createGradient(warning.light, warning.main),
  error: createGradient(error.light, error.main),
};

const chart = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const base = {
  common: { black: '#000', white: '#fff' },
  primary,
  secondary,
  info,
  success,
  warning,
  error,
  impersonation,
  konnect,
  grey,
  gradients,
  chart,
  divider: grey[500_24],
  action: {
    hover: grey[500_8],
    selected: grey[500_16],
    disabled: grey[500_80],
    disabledBackground: grey[500_24],
    focus: grey[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
} as const satisfies DeepPartial<PaletteOptions>;

const palette = {
  light: {
    ...base,
    mode: 'light',
    text: { primary: grey[800], secondary: grey[600], disabled: grey[500] },
    background: { paper: '#fff', default: '#fff', neutral: grey[200] },
    action: { active: grey[600], ...base.action },
    navBar: {
      menuActiveBg: primary.lighter,
      menuActiveText: primary.main,
      menuDefaultText: grey[600],
      menuHoverBg: primary.lighter,
      menuHoverText: grey[600],
      menuSecondaryText: grey[600],
    },
    note: { paper: '#FFFEF5' },
  },
  dark: {
    ...base,
    mode: 'dark',
    text: { primary: '#fff', secondary: grey[500], disabled: grey[600] },
    background: { paper: grey[800], default: grey[900], neutral: grey[500_16] },
    action: { active: grey[500], ...base.action },
    navBar: {
      menuActiveBg: primary.lighter,
      menuActiveText: primary.main,
      menuDefaultText: grey[500],
      menuHoverBg: primary.lighter,
      menuHoverText: grey[500],
      menuSecondaryText: grey[600],
    },
    note: { paper: grey[800] },
  },
} as const satisfies { light: PaletteOptions; dark: PaletteOptions };

export default palette;
